import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { useMutation, useQuery } from '@apollo/client';
import { CREATE_CALENDAR_RULERS, GET_CALENDAR_RULER_BY_ID, UPDATE_CALENDAR_RULERS } from '~/services/schedule';
import { LIST_SHARED_AGENTS } from '~/services';
import { GET_GOOGLE_CALENDAR_INTEGRATION } from '~/services/integration';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

// Define enum locally to match the one from @tbit-io/types
enum meetingType {
  VIRTUAL = "VIRTUAL",
  IN_PERSON = "IN_PERSON",
}

interface TimeBlock {
  start: { hour: number; minute: number };
  end: { hour: number; minute: number };
  active: boolean;
}

interface ScheduleItem {
  start: { hour: number; minute: number };
  end: { hour: number; minute: number };
  active: boolean;
  days: number[];
  _id?: string;
}

interface DayAvailability {
  dayIndex: number;
  dayName: string;
  active: boolean;
  timeBlocks: TimeBlock[];
}

interface CalendarRulerData {
  meetingDetails?: {
    link?: string;
    headquarters?: string;
    address?: string;
    phone?: string;
  };
  _id: string;
  owner_id: string;
  agent_id: string;
  available_schedule: ScheduleItem[];
  blockers_schedule: any[];
  duration_of_meeting: number;
  minimum_scheduling_time: number;
  utc: number;
  created_at: string;
  updated_at: string;
  type: string;
  max_users_per_meeting: number;
  meetingType: meetingType;
  meeting_duration_in_mins: number;
  description: string;
  title: string;
  isEnabled: boolean;
  isCustomSchedule?: boolean;
  calendarIntegrations?: {
    googleCalendarIntegration?: string;
  }[];
}

interface GoogleCalendarIntegration {
  name: string;
  email: string;
  calendarId: string;
  integration_id: string;
}

interface BackendGoogleCalendarIntegration {
  id: string;
  type: string;
  agentId: string;
  configuration: {
    notification_emails?: string[];
    googleCalendarIntegration?: GoogleCalendarIntegration[];
  };
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

const CreateSchedule: React.FC = () => {
  const [t] = useTranslation('schedule');
  const { agentId, scheduleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!scheduleId;
  
  // Check if this is the first schedule creation
  const isFirst = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('first') === 'true';
  }, [location.search]);
  
  // Update day names when language changes
  useEffect(() => {
    setDaysAvailability(prevDays => 
      prevDays.map(day => ({
        ...day,
        dayName: t(day.dayIndex === 0 ? 'monday' : 
                  day.dayIndex === 1 ? 'tuesday' :
                  day.dayIndex === 2 ? 'wednesday' :
                  day.dayIndex === 3 ? 'thursday' :
                  day.dayIndex === 4 ? 'friday' :
                  day.dayIndex === 5 ? 'saturday' : 'sunday')
      }))
    );
  }, [t]);
  
  // Log when this is the first schedule
  useEffect(() => {
    if (isFirst) {
      console.log('Creating first schedule - isFirst:', isFirst);
    }
  }, [isFirst]);
  
  // Fetch schedule data when in edit mode
  const { data: scheduleData, loading: fetchingSchedule } = useQuery(
    GET_CALENDAR_RULER_BY_ID, 
    {
      variables: {
        data: { calendar_id: scheduleId }
      },
      skip: !isEditMode,
      fetchPolicy: 'network-only',
    }
  );
  
  // Log when in edit mode and populate form when data is fetched
  useEffect(() => {
    if (isEditMode) {      
      // When schedule data is loaded from GET_CALENDAR_RULER_BY_ID
      if (isEditMode && scheduleData?.get_calendar_ruler_by_id?.data && !scheduleData.get_calendar_ruler_by_id.hasError) {
        const schedule = scheduleData.get_calendar_ruler_by_id.data as CalendarRulerData;
        
        if (schedule) {          
          // Populate form fields with schedule data
          setTitle(schedule.title);
          setDescription(schedule.description);
          setSelectedMeetingType(schedule.meetingType);
          setMeetingDuration(schedule.duration_of_meeting);
          setMaxPeoplePerMeeting(schedule.max_users_per_meeting);
          setSelectedTimezone(schedule.utc); // Set the timezone value
          
          // Populate meeting details based on meeting type
          if (schedule.meetingType === meetingType.VIRTUAL && schedule.calendarIntegrations?.length) {
            // Set the selected Google Calendar integration from the saved schedule
            const googleCalendarIntegration = schedule.calendarIntegrations[0]?.googleCalendarIntegration;
            if (googleCalendarIntegration) {
              setSelectedGoogleCalendarIntegration(googleCalendarIntegration);
            }
          } else if (schedule.meetingType === meetingType.IN_PERSON) {
            if (schedule.meetingDetails?.headquarters) setHeadquartersName(schedule.meetingDetails.headquarters);
            if (schedule.meetingDetails?.address) setMeetingAddress(schedule.meetingDetails.address);
            if (schedule.meetingDetails?.phone) setContactPhone(schedule.meetingDetails.phone);
          }
          
          // Set customize hours toggle based on the isCustomSchedule flag from the server
          // Default to true if not defined for backward compatibility
          setShowCustomHours(schedule.isCustomSchedule !== undefined ? schedule.isCustomSchedule : true);
          
          // Process available schedule and update days availability
          if (schedule.available_schedule && schedule.available_schedule.length > 0) {
            console.log('Processing available schedule:', schedule.available_schedule);
            
            // Create a new copy of days availability
            const updatedDays = [...daysAvailability];
            
            // First, we need to organize time blocks by day for better processing
            const timeBlocksByDay = new Map<number, TimeBlock[]>();
            
            // Process each available schedule entry and organize by day
            schedule.available_schedule.forEach((scheduleItem: ScheduleItem) => {
              // For each day in this schedule item
              scheduleItem.days.forEach((day: number) => {
                const dayIndex = day; // Backend uses 0-6 for days, same as our frontend
                
                // Make sure it's a valid day index
                if (dayIndex >= 0 && dayIndex <= 6) {
                  console.log(`Activating day ${dayIndex} (${updatedDays[dayIndex].dayName})`);
                  
                  // Set the day as active
                  updatedDays[dayIndex].active = true;
                  
                  // Create the time block
                  const timeBlock = {
                    start: scheduleItem.start,
                    end: scheduleItem.end,
                    active: scheduleItem.active
                  };
                  
                  // Add this time block to the day's array
                  if (!timeBlocksByDay.has(dayIndex)) {
                    timeBlocksByDay.set(dayIndex, [timeBlock]);
                  } else {
                    const existingBlocks = timeBlocksByDay.get(dayIndex) || [];
                    
                    // Check if this exact time block already exists to avoid duplicates
                    const blockExists = existingBlocks.some(
                      block => 
                        block.start.hour === timeBlock.start.hour && 
                        block.start.minute === timeBlock.start.minute && 
                        block.end.hour === timeBlock.end.hour && 
                        block.end.minute === timeBlock.end.minute
                    );
                    
                    // Only add if it doesn't exist already
                    if (!blockExists) {
                      existingBlocks.push(timeBlock);
                      timeBlocksByDay.set(dayIndex, existingBlocks);
                    }
                  }
                }
              });
            });
            
            // Now, apply the organized time blocks to each day
            timeBlocksByDay.forEach((timeBlocks, dayIndex) => {
              // Replace the default time block with our custom ones
              if (timeBlocks.length > 0) {
                updatedDays[dayIndex].timeBlocks = timeBlocks;
              }
            });
            
            // Update the state with the new days availability
            setDaysAvailability(updatedDays);
            
            // Set active tab to the first active day
            const firstActiveDay = updatedDays.findIndex(day => day.active);
            if (firstActiveDay !== -1) {
              setActiveTabDay(firstActiveDay);
            }
          }
        }
      }
    }
  }, [isEditMode, scheduleId, scheduleData]);
    
  // Create and update mutations
  const [createCalendarRulers, { loading: createLoading, error: createError }] = useMutation(
    CREATE_CALENDAR_RULERS,
  );
  const [updateCalendarRulers, { loading: updateLoading, error: updateError }] = useMutation(
    UPDATE_CALENDAR_RULERS,
  );
  
  // Combined loading and error states
  const loading = createLoading || updateLoading;
  const error = createError || updateError;
  
  // Success message state
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMeetingType, setSelectedMeetingType] = useState<meetingType>(meetingType.IN_PERSON); // Default to IN_PERSON
  const [showCustomHours, setShowCustomHours] = useState(false);
  const [activeTabDay, setActiveTabDay] = useState(0);
  const [meetingDuration, setMeetingDuration] = useState(30); // Duration in minutes
  const [maxPeoplePerMeeting, setMaxPeoplePerMeeting] = useState(1); // Default to 1 person
  const [selectedGoogleCalendarIntegration, setSelectedGoogleCalendarIntegration] = useState<string>('');
  const [headquartersName, setHeadquartersName] = useState('');
  const [meetingAddress, setMeetingAddress] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [meetingNotice, setMeetingNotice] = useState(15); // Minimum scheduling time in minutes
  const [selectedTimezone, setSelectedTimezone] = useState(new Date().getTimezoneOffset() / -60); // Default to current system timezone
  const [virtualMeetingError, setVirtualMeetingError] = useState<string>(''); // Error state for virtual meeting validation
  
  // Initialize with one default time block (8:00 AM - 7:00 PM)
  const defaultTimeBlock: TimeBlock = {
    start: { hour: 8, minute: 0 },
    end: { hour: 19, minute: 0 },
    active: true
  };
  
  // Initialize days with translations
  const [daysAvailability, setDaysAvailability] = useState<DayAvailability[]>([
    { dayIndex: 0, dayName: t('monday'), active: false, timeBlocks: [defaultTimeBlock] },
    { dayIndex: 1, dayName: t('tuesday'), active: false, timeBlocks: [defaultTimeBlock] },
    { dayIndex: 2, dayName: t('wednesday'), active: false, timeBlocks: [defaultTimeBlock] },
    { dayIndex: 3, dayName: t('thursday'), active: false, timeBlocks: [defaultTimeBlock] },
    { dayIndex: 4, dayName: t('friday'), active: false, timeBlocks: [defaultTimeBlock] },
    { dayIndex: 5, dayName: t('saturday'), active: false, timeBlocks: [defaultTimeBlock] },
    { dayIndex: 6, dayName: t('sunday'), active: false, timeBlocks: [defaultTimeBlock] }
  ]);

  // Fetch shared agents
  const { data: sharedAgentsData, loading: sharedAgentsLoading } = useQuery(LIST_SHARED_AGENTS, {
    variables: { data: { agent_id: agentId } },
  });

  // Fetch Google Calendar integrations
  const { data: googleCalendarData, loading: googleCalendarLoading } = useQuery(GET_GOOGLE_CALENDAR_INTEGRATION, {
    variables: { data: { agentId: agentId } },
    skip: !agentId,
    fetchPolicy: 'network-only',
  });

  // Extract Google Calendar integrations
  const googleCalendarIntegrations: GoogleCalendarIntegration[] = useMemo(() => {
    if (googleCalendarData?.get_google_calendar_integration?.data?.integration?.configuration?.googleCalendarIntegration) {
      return googleCalendarData.get_google_calendar_integration.data.integration.configuration.googleCalendarIntegration;
    }
    return [];
  }, [googleCalendarData]);

  // Check if user has Google Calendar integrations
  const hasGoogleCalendarIntegration = googleCalendarIntegrations.length > 0;

  // Effect to handle meeting type based on integrations availability
  useEffect(() => {
    // Clear any previous errors when meeting type changes
    setVirtualMeetingError('');
  }, [selectedMeetingType]);

  // Effect to set default meeting type based on integrations availability (only on initial load)
  useEffect(() => {
    // Only set default if it's not in edit mode and we haven't manually changed the meeting type
    if (!isEditMode && !googleCalendarLoading) {
      if (hasGoogleCalendarIntegration && selectedMeetingType === meetingType.IN_PERSON) {
        // If user has integration and is still on the default IN_PERSON, switch to VIRTUAL
        setSelectedMeetingType(meetingType.VIRTUAL);
        // Auto-select the first available integration
        if (googleCalendarIntegrations.length > 0) {
          setSelectedGoogleCalendarIntegration(googleCalendarIntegrations[0].integration_id);
        }
      }
    }
  }, [hasGoogleCalendarIntegration, googleCalendarLoading, isEditMode, googleCalendarIntegrations]);

  // Handle meeting type change
  const handleMeetingTypeChange = (newMeetingType: meetingType) => {
    setVirtualMeetingError('');
    setSelectedMeetingType(newMeetingType);
  };

  // Define the type for EmailList entries
  interface EmailEntry {
    email: string;
    id: string;
  }

  // Extract emails and IDs from shared agents
  const EmailList: EmailEntry[] = useMemo(() => {
    if (sharedAgentsData?.list_shared_agents?.data) {
      return sharedAgentsData.list_shared_agents.data.map((agent: { email: string; _id: string }) => ({
        email: agent.email,
        id: agent._id,
      }));
    }
    return [];
  }, [sharedAgentsData]);

  // Initialize selectedEmails with the first email from EmailList
  const [selectedEmails, setSelectedEmails] = useState<EmailEntry[]>([]);
  useEffect(() => {
    // For edit mode: populate selectedEmails from notificationEmails in the fetched schedule data
    if (isEditMode && scheduleData?.get_calendar_ruler_by_id?.data?.notificationEmails) {
      const notificationEmails = scheduleData.get_calendar_ruler_by_id.data.notificationEmails;
      setSelectedEmails(notificationEmails.map((email: any) => ({
        email: email.email,
        id: email.id
      })));
    }
    // For create mode: use the first email from EmailList as default
    else if (EmailList.length > 0 && selectedEmails.length === 0 && !isEditMode) {
      setSelectedEmails([EmailList[0]]);
    }
  }, [EmailList, scheduleData, isEditMode]);

  // Function to handle adding emails
  const addEmail = (email: string) => {
    const emailEntry = EmailList.find((entry) => entry.email === email);
    if (emailEntry && !selectedEmails.some((selected) => selected.email === email)) {
      setSelectedEmails([...selectedEmails, emailEntry]);
    }
  };

  // Function to handle removing emails
  const removeEmail = (email: string) => {
    setSelectedEmails(selectedEmails.filter((e) => e.email !== email));
  };

  const toggleDayActive = (dayIndex: number) => {
    const updatedDays = [...daysAvailability];
    const day = updatedDays[dayIndex];
    
    day.active = !day.active;
    
    // If the day is being activated, set it as the active tab
    if (day.active) {
      setActiveTabDay(dayIndex);
    } else {
      // If the current active tab is being deactivated, find a new active tab
      if (activeTabDay === dayIndex) {
        const nextActiveDay = updatedDays.find((d, i) => d.active && i !== dayIndex);
        if (nextActiveDay) {
          setActiveTabDay(nextActiveDay.dayIndex);
        }
      }
    }
    
    setDaysAvailability(updatedDays);
  };
  
  // Add a new time block to a specific day
  const addTimeBlock = (dayIndex: number) => {
    const updatedDays = [...daysAvailability];
    const day = updatedDays[dayIndex];
    
    // Create a new time block with default values (9:00 AM - 5:00 PM)
    const newTimeBlock: TimeBlock = {
      start: { hour: 9, minute: 0 },
      end: { hour: 17, minute: 0 },
      active: true
    };
    
    day.timeBlocks.push(newTimeBlock);
    setDaysAvailability(updatedDays);
  };
  
  // Remove a time block from a specific day
  const removeTimeBlock = (dayIndex: number, blockIndex: number) => {
    const updatedDays = [...daysAvailability];
    updatedDays[dayIndex].timeBlocks.splice(blockIndex, 1);
    
    // Ensure there's always at least one time block
    if (updatedDays[dayIndex].timeBlocks.length === 0) {
      updatedDays[dayIndex].timeBlocks.push({
        start: { hour: 8, minute: 0 },
        end: { hour: 19, minute: 0 },
        active: true
      });
    }
    
    setDaysAvailability(updatedDays);
  };
  
  // Update start time for a specific time block
  const updateStartTime = (dayIndex: number, blockIndex: number, hour: number, minute: number) => {
    const updatedDays = [...daysAvailability];
    updatedDays[dayIndex].timeBlocks[blockIndex].start = { hour, minute };
    setDaysAvailability(updatedDays);
  };
  
  // Update end time for a specific time block
  const updateEndTime = (dayIndex: number, blockIndex: number, hour: number, minute: number) => {
    const updatedDays = [...daysAvailability];
    updatedDays[dayIndex].timeBlocks[blockIndex].end = { hour, minute };
    setDaysAvailability(updatedDays);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    setVirtualMeetingError('');
    
    // Validate virtual meeting requirements
    if (selectedMeetingType === meetingType.VIRTUAL && !selectedGoogleCalendarIntegration) {
      setVirtualMeetingError(t('virtual_meeting_requires_google_calendar') || 'Virtual meetings require a Google Calendar integration to be selected.');
      return;
    }
    
    try {
      // Convert form data to CalendarRulers structure - optimized to group by time blocks
      let availableSchedule: ScheduleItem[] = [];
      
      if (showCustomHours) {
        // CUSTOM HOURS MODE: Each day has its own specific time blocks
        // Process each day separately
        daysAvailability.forEach(day => {
          if (day.active) {
            // For each time block in this day, create a separate schedule item
            day.timeBlocks.forEach(block => {
              availableSchedule.push({
                start: block.start,
                end: block.end,
                active: true,
                days: [day.dayIndex] // Backend uses 0-6 for days (domingo a sábado)
              });
            });
          }
        });
      } else {
        // STANDARD MODE: All active days share the same time blocks
        // Get the time blocks from the first active day (they're all the same in this mode)
        const firstActiveDay = daysAvailability.find(day => day.active);
        if (firstActiveDay) {
          // For each time block, create an entry with all active days
          firstActiveDay.timeBlocks.forEach(block => {
            // Collect all active day indices
            const activeDayIndices = daysAvailability
              .filter(day => day.active)
              .map(day => day.dayIndex); // Backend uses 0-6 for days
            
            // Create a single entry with all active days
            availableSchedule.push({
              start: block.start,
              end: block.end,
              active: true,
              days: activeDayIndices
            });
          });
        }
      }
      
      // Prepare meeting details and calendar integration based on meeting type
      const meetingDetails = selectedMeetingType === meetingType.IN_PERSON 
        ? {
            headquarters: headquartersName,
            address: meetingAddress,
            phone: contactPhone
          }
        : undefined;

      // Prepare calendar integration for virtual meetings
      const calendarIntegrations = selectedMeetingType === meetingType.VIRTUAL && selectedGoogleCalendarIntegration
        ? [{ googleCalendarIntegration: selectedGoogleCalendarIntegration }]
        : undefined;
      
      // Prepare the mutation input data
      const calendarRulersData = {
        agent_id: agentId,
        available_schedule: availableSchedule,
        utc: selectedTimezone, // Use the selected timezone value
        blockers_schedule: [], // Add blockers if implemented
        duration_of_meeting: meetingDuration,
        meeting_duration_in_mins: meetingDuration, // Same as duration_of_meeting
        minimum_scheduling_time: meetingNotice * 60, // Convert to seconds
        max_users_per_meeting: maxPeoplePerMeeting,
        description: description,
        title: title,
        type: 'PERSONAL', // or 'TEAM' if team calendar
        meetingType: selectedMeetingType,
        meetingDetails: meetingDetails,
        isCustomSchedule: showCustomHours, // Flag to determine if we're using custom hours per day
        notificationEmails: selectedEmails, // Add selected emails here
        isFirst: isFirst, // Flag to indicate if this is the first schedule
        ...(calendarIntegrations && {
          calendarIntegrations: calendarIntegrations
        })
      };
      
      // Log for debugging
      if (isEditMode) {
        
        const updateData = {
          ...calendarRulersData,
          id: scheduleId,
          isEnabled: true // Always set isEnabled to true when updating
        };
        console.log('Update data:', updateData);
        
        // Make sure isCustomSchedule is included in the data
        updateData.isCustomSchedule = showCustomHours;
        
        console.log('Updating with isCustomSchedule explicitly set:', updateData.isCustomSchedule);
        
        // Execute the update mutation
        const { data } = await updateCalendarRulers({
          variables: { data: updateData }
        });
        
        // Handle response
        if (data && !data.update_calendar_rulers.hasError) {
          // Navigate to the schedule home page with success parameter
          navigate(`/agent/${agentId}/schedule?action=updated`);
          return;
        } else {
          console.error('Error updating calendar ruler:', data?.update_calendar_rulers.message);
          // Error will be shown through the error state at the end of the form
          return;
        }
      } else {
        // Create new schedule
        // Make sure isCustomSchedule is included in the data
        calendarRulersData.isCustomSchedule = showCustomHours;
                
        // Execute the mutation
        const { data } = await createCalendarRulers({
          variables: {
            data: calendarRulersData
          }
        });
        
        // Handle response
        if (data && !data.create_calendar_rulers.hasError) {
          // Navigate to the schedule home page with success parameter
          navigate(`/agent/${agentId}/schedule?action=created`);
        } else {
          console.error('Error creating calendar rulers:', data?.create_calendar_rulers.message);
          // Error will be shown through the error state at the end of the form
        }
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      // Error will be handled by the Apollo error state and shown at the end of the form
    }
  };

  const formatTime = (hour: number, minute: number) => {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  // Generate timezone options
  const timezoneOptions = useMemo(() => {
    // Get all timezone names from moment-timezone
    const timezones = moment.tz.names();
    
    // Create a map to group timezones by offset
    const timezoneMap = new Map<number, { name: string, offset: number, label: string, city: string }[]>();
    
    // Process each timezone
    timezones.forEach(timezone => {
      const offset = moment.tz(timezone).utcOffset() / 60; // Convert minutes to hours
      const existing = timezoneMap.get(offset) || [];
      
      // Extract city name from timezone
      const city = timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
      
      existing.push({
        name: timezone,
        offset,
        label: `(UTC${offset >= 0 ? '+' : ''}${offset}) ${timezone.replace(/_/g, ' ')}`,
        city
      });
      
      timezoneMap.set(offset, existing);
    });
    
    // Convert map to array and sort by offset
    const result = Array.from(timezoneMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([offset, zones]) => {
        // Get up to 3 major cities for this offset
        const cities = zones
          .filter(zone => zone.name.includes('/')) // Only include proper timezone names with cities
          .sort((a, b) => {
            // Sort by popularity/recognition of cities (capitals and major cities first)
            const popularCities = ['New_York', 'Los_Angeles', 'Chicago', 'London', 'Paris', 'Berlin', 'Madrid', 
                                  'Rome', 'Moscow', 'Tokyo', 'Beijing', 'Shanghai', 'Hong_Kong', 'Singapore', 
                                  'Sydney', 'Melbourne', 'Auckland', 'Dubai', 'Mumbai', 'Delhi', 'Bangkok', 
                                  'Cairo', 'Lagos', 'Johannesburg', 'Sao_Paulo', 'Mexico_City', 'Lima', 'Santiago',
                                  'Bogota', 'Buenos_Aires', 'Toronto', 'Vancouver', 'Montreal', 'Istanbul', 'Athens'];
            
            const cityA = a.name.split('/').pop() || '';
            const cityB = b.name.split('/').pop() || '';
            
            const indexA = popularCities.findIndex(city => cityA.includes(city));
            const indexB = popularCities.findIndex(city => cityB.includes(city));
            
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            
            return cityA.localeCompare(cityB);
          })
          .slice(0, 3) // Take the top 3 cities
          .map(zone => zone.city);
        
        // Create label with offset and cities
        const cityText = cities.length > 0 ? ` (${cities.join(', ')})` : '';
        
        return {
          offset,
          label: `UTC${offset >= 0 ? '+' : ''}${offset}${cityText}`,
          zones
        };
      });
    
    return result;
  }, []);

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">
        {isEditMode ? t('edit_schedule') : t('create_schedule')}
        {fetchingSchedule && isEditMode && (
          <span className="loading loading-spinner loading-sm ml-2"></span>
        )}
      </h2>
      
      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="mb-4">
          <label className="text-base mb-2 block">{t('schedule_name')}</label>
          <input 
            type="text" 
            className="input input-bordered w-full" 
            placeholder={t('schedule_name')} 
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required 
          />
        </div>
        
        <div className="mb-4">
          <label className="text-base mb-2 block">{t('schedule_description_field')}</label>
          <textarea 
            className="textarea textarea-bordered w-full h-24" 
            placeholder={t('schedule_description_field')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          ></textarea>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-6">
          <div className="mb-2">
            <label className="text-base mb-2 block">{t('meeting_duration')}</label>
            <select 
              className="select select-bordered w-full" 
              value={meetingDuration}
              onChange={(e) => setMeetingDuration(Number(e.target.value))}
            >
              <option value="15">15 {t('minutes')}</option>
              <option value="30">30 {t('minutes')}</option>
              <option value="45">45 {t('minutes')}</option>
              <option value="60">60 {t('minutes')} (1 {t('hour')})</option>
              <option value="90">90 {t('minutes')} (1.5 {t('hours')})</option>
              <option value="120">120 {t('minutes')} (2 {t('hours')})</option>
            </select>
          </div>
          
          <div className="mb-2">
            <label className="text-base mb-2 block">{t('max_people_per_meeting')}</label>
            <div className="flex items-center">
              <button 
                type="button" 
                className="btn btn-outline btn-sm"
                disabled={maxPeoplePerMeeting <= 1} 
                onClick={() => setMaxPeoplePerMeeting(prev => Math.max(1, prev - 1))}
              >-</button>
              <input 
                type="number" 
                className="input input-bordered w-24 mx-2 text-center" 
                min="1" 
                max="100"
                value={maxPeoplePerMeeting} 
                onChange={(e) => setMaxPeoplePerMeeting(Number(e.target.value))}
              />
              <button 
                type="button" 
                className="btn btn-outline btn-sm"
                onClick={() => setMaxPeoplePerMeeting(prev => Math.min(100, prev + 1))}
              >+</button>
              <span className="ml-2">{t('people')}</span>
            </div>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="text-base mb-2 block">{t('timezone') || 'Timezone'}</label>
          <select 
            className="select select-bordered w-full" 
            value={selectedTimezone}
            onChange={(e) => setSelectedTimezone(Number(e.target.value))}
          >
            {timezoneOptions.map((option) => (
              <option key={option.offset} value={option.offset}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-sm mt-1 text-gray-500">
            {t('timezone_description') || 'Select the timezone for your schedule. Cities are shown for reference.'}
          </p>
        </div>
        
        {/* Google Calendar Integration - Always shown */}
        <div className="mb-6">
          <label className="text-base mb-2 block">{t('google_calendar_integration') || 'Google Calendar Integration'}</label>
          {googleCalendarLoading ? (
            <div className="flex items-center gap-2">
              <span className="loading loading-spinner loading-sm"></span>
              <span>{t('loading_integrations') || 'Loading integrations...'}</span>
            </div>
          ) : googleCalendarIntegrations.length > 0 ? (
            <>
              <select 
                className="select select-bordered w-full" 
                value={selectedGoogleCalendarIntegration}
                onChange={(e) => {
                  setSelectedGoogleCalendarIntegration(e.target.value);
                  setVirtualMeetingError(''); // Clear error when selecting an integration
                }}
              >
                <option value="">{t('select_calendar_integration') || 'Select a calendar integration'}</option>
                {googleCalendarIntegrations.map((integration) => (
                  <option key={integration.integration_id} value={integration.integration_id}>
                    {integration.name} ({integration.email})
                  </option>
                ))}
              </select>
              <p className="text-sm mt-1 text-gray-500">
                {t('calendar_integration_description') || 'Select which Google Calendar integration to use for meetings.'}
              </p>
            </>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-yellow-800 mb-1">
                    {t('no_calendar_integrations') || 'No Google Calendar integrations found'}
                  </h4>
                  <p className="text-sm text-yellow-700 mb-3">
                    {t('need_calendar_integration') || 'Create a Google Calendar integration to automatically sync appointments and generate meeting links.'}
                  </p>
                  <button
                    type="button"
                    className="btn btn-sm btn-warning"
                    onClick={() => navigate(`/agent/${agentId}/schedule/apps`)}
                  >
                    {t('go_to_integrations') || 'Go to Apps & Integrations'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="mb-4">
          <label className="text-base mb-2 block">{t('meeting_type')}</label>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio" 
                className="radio radio-primary" 
                name="meeting-type" 
                checked={selectedMeetingType === meetingType.VIRTUAL}
                onChange={() => handleMeetingTypeChange(meetingType.VIRTUAL)}
              />
              <span>{t('virtual_meeting')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio" 
                className="radio radio-primary" 
                name="meeting-type"
                checked={selectedMeetingType === meetingType.IN_PERSON}
                onChange={() => handleMeetingTypeChange(meetingType.IN_PERSON)}
              />
              <span>{t('in_person_meeting')}</span>
            </label>
          </div>
          
          {/* Info message for virtual meetings */}
          {selectedMeetingType === meetingType.VIRTUAL && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-blue-800 mb-1">
                    {t('virtual_meeting_info_title') || 'Virtual Meeting Setup'}
                  </h4>
                  <p className="text-sm text-blue-700">
                    {t('virtual_meeting_info_description') || 'We will automatically create a new meeting link and send it to the person. The appointment will also be created in your selected Google Calendar.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Error message for virtual meeting validation */}
          {virtualMeetingError && (
            <div className="alert alert-error mt-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{virtualMeetingError}</span>
            </div>
          )}
        </div>
        
        {/* Meeting details for in-person meetings only */}
        {selectedMeetingType === meetingType.IN_PERSON && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mb-6">
            <div className="mb-2">
              <label className="text-base mb-2 block">{t('headquarters')}</label>
              <input 
                type="text" 
                className="input input-bordered w-full" 
                placeholder={t('headquarters_placeholder')} 
                value={headquartersName}
                onChange={(e) => setHeadquartersName(e.target.value)}
                required
              />
            </div>
            <div className="mb-2">
              <label className="text-base mb-2 block">{t('phone')}</label>
              <input 
                type="tel" 
                className="input input-bordered w-full" 
                placeholder="+123456789" 
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </div>
            <div className="md:col-span-2 mb-2">
              <label className="text-base mb-2 block">{t('address')}</label>
              <textarea 
                className="textarea textarea-bordered w-full" 
                placeholder={t('full_address')}
                value={meetingAddress}
                onChange={(e) => setMeetingAddress(e.target.value)}
                required
              ></textarea>
            </div>
          </div>
        )}
        
        <div className="mb-4">
          <label className="text-base mb-2 block">{t('notification_emails') || 'Notification Emails'}</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {selectedEmails.map(({ email }) => (
              <span
                key={email}
                className="badge badge-secondary flex items-center gap-2 bg-purple-200 text-purple-800"
              >
                {email}
                <button
                  type="button"
                  className="text-purple-800 hover:text-purple-600"
                  onClick={() => removeEmail(email)}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            className="input input-bordered w-full"
            placeholder={t('type_and_select_email') || 'Type and select an email'}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addEmail(e.currentTarget.value);
                e.currentTarget.value = '';
              }
            }}
            onChange={(e) => {
              const email = e.target.value;
              if (EmailList.some((entry) => entry.email === email)) {
                addEmail(email);
                e.target.value = '';
              }
            }}
            list="email-options"
          />
          <datalist id="email-options">
            {EmailList.map(({ email }) => (
              <option key={email} value={email} />
            ))}
          </datalist>
          <p 
            className="text-sm mt-1 text-gray-500" 
            dangerouslySetInnerHTML={{ 
              __html: t('select_emails_to_notify', { agentId })
                .replace('{agentId}', agentId || '') || 
              'Type and select emails to notify about this schedule.'
            }}
          ></p>
        </div>
        
        <div className="mb-6">
          <label className="text-base mb-2 block">{t('available_hours')}</label>
          
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                className="toggle toggle-primary" 
                checked={showCustomHours}
                onChange={() => setShowCustomHours(!showCustomHours)}
              />
              <span>{t('customize_hours_per_day')}</span>
            </label>
          </div>
          
          <div className="bg-base-200 p-6 rounded-lg mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-3 gap-x-6 mb-6">
              {daysAvailability.map((day, dayIndex) => (
                <div key={dayIndex}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="checkbox checkbox-primary" 
                      checked={day.active}
                      onChange={() => toggleDayActive(dayIndex)}
                    />
                    <span>{day.dayName}</span>
                  </label>
                </div>
              ))}
            </div>
            
            {daysAvailability.filter(day => day.active).length > 0 ? (
              <div className="mt-6">
                {showCustomHours && (
                  <>
                    <div className="tabs tabs-boxed mb-5">
                      {daysAvailability.filter(day => day.active).map((day, index) => (
                        <a 
                          key={day.dayIndex} 
                          className={`tab cursor-pointer ${activeTabDay === day.dayIndex ? 'tab-active' : ''}`}
                          onClick={() => setActiveTabDay(day.dayIndex)}
                        >
                          {day.dayName}
                        </a>
                      ))}
                    </div>
                    
                    {daysAvailability.filter(day => day.active).map(day => (
                      <div key={day.dayIndex} className={activeTabDay === day.dayIndex ? 'block' : 'hidden'}>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium">{day.dayName} {t('time_slots')}</h3>
                          <button 
                            type="button" 
                            className="btn btn-primary btn-sm"
                            onClick={() => addTimeBlock(day.dayIndex)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        
                        <div className="space-y-4">
                          {day.timeBlocks.map((block, blockIndex) => (
                            <div key={blockIndex} className="flex flex-wrap items-center gap-4 p-4 bg-base-100 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span>{t('desde')}</span>
                                <div className="relative w-40">
                                  <input
                                    type="time"
                                    className="input input-bordered w-full"
                                    value={`${block.start.hour.toString().padStart(2, '0')}:${block.start.minute.toString().padStart(2, '0')}`}
                                    onChange={(e) => {
                                      const [hours, minutes] = e.target.value.split(':').map(Number);
                                      updateStartTime(day.dayIndex, blockIndex, hours, minutes);
                                    }}
                                  />
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <span>{t('hasta')}</span>
                                <div className="relative w-40">
                                  <input
                                    type="time"
                                    className="input input-bordered w-full"
                                    value={`${block.end.hour.toString().padStart(2, '0')}:${block.end.minute.toString().padStart(2, '0')}`}
                                    onChange={(e) => {
                                      const [hours, minutes] = e.target.value.split(':').map(Number);
                                      updateEndTime(day.dayIndex, blockIndex, hours, minutes);
                                    }}
                                  />
                                </div>
                              </div>
                              
                              {day.timeBlocks.length > 1 && (
                                <button 
                                  type="button" 
                                  className="btn btn-sm btn-error"
                                  onClick={() => removeTimeBlock(day.dayIndex, blockIndex)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ) : (
              <p className="text-center text-gray-500 my-6">{t('select_days_first')}</p>
            )}
            
            {!showCustomHours && daysAvailability.filter(day => day.active).length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">{t('standard_time_slots')}</h3>
                  <button 
                    type="button" 
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      // Add a new time block to all active days
                      const newBlock: TimeBlock = {
                        start: { hour: 9, minute: 0 },
                        end: { hour: 17, minute: 0 },
                        active: true
                      };
                      
                      const updatedDays = [...daysAvailability];
                      updatedDays.forEach(day => {
                        if (day.active) {
                          day.timeBlocks.push({...newBlock});
                        }
                      });
                      setDaysAvailability(updatedDays);
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                
                {/* Get time blocks from the first active day to use as template */}
                {daysAvailability.find(day => day.active)?.timeBlocks.map((block, blockIndex) => (
                  <div key={blockIndex} className="flex flex-wrap items-center gap-4 p-4 bg-base-100 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span>{t('desde')}</span>
                      <div className="relative w-40">
                        <input
                          type="time"
                          className="input input-bordered w-full"
                          value={`${block.start.hour.toString().padStart(2, '0')}:${block.start.minute.toString().padStart(2, '0')}`}
                          onChange={(e) => {
                            const [hours, minutes] = e.target.value.split(':').map(Number);
                            const updatedDays = [...daysAvailability];
                            updatedDays.forEach(day => {
                              if (day.active && day.timeBlocks[blockIndex]) {
                                day.timeBlocks[blockIndex].start = { hour: hours, minute: minutes };
                              }
                            });
                            setDaysAvailability(updatedDays);
                          }}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span>{t('hasta')}</span>
                      <div className="relative w-40">
                        <input
                          type="time"
                          className="input input-bordered w-full"
                          value={`${block.end.hour.toString().padStart(2, '0')}:${block.end.minute.toString().padStart(2, '0')}`}
                          onChange={(e) => {
                            const [hours, minutes] = e.target.value.split(':').map(Number);
                            const updatedDays = [...daysAvailability];
                            updatedDays.forEach(day => {
                              if (day.active && day.timeBlocks[blockIndex]) {
                                day.timeBlocks[blockIndex].end = { hour: hours, minute: minutes };
                              }
                            });
                            setDaysAvailability(updatedDays);
                          }}
                        />
                      </div>
                    </div>
                    
                    {(daysAvailability.find(day => day.active)?.timeBlocks.length || 0) > 1 && (
                      <button 
                        type="button" 
                        className="btn btn-sm btn-error"
                        onClick={() => {
                          // Remove this time block from all active days
                          const updatedDays = [...daysAvailability];
                          updatedDays.forEach(day => {
                            if (day.active && day.timeBlocks.length > 1) {
                              day.timeBlocks.splice(blockIndex, 1);
                            }
                          });
                          setDaysAvailability(updatedDays);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-4">
          <button 
            type="submit" 
            className="btn btn-primary px-8"
            disabled={loading || !daysAvailability.some(day => day.active)}
          >
            {loading ? <span className="loading loading-spinner"></span> : t('save')}
          </button>
          <button 
            type="button" 
            className="btn btn-outline px-8"
            onClick={() => {
              if (isEditMode) {
                // Si estamos en modo edición, simplemente volver a cargar los datos del servidor
                // para restaurar la versión original
                if (window.confirm(t('cancel_confirm'))) {
                  window.location.reload();
                }
              } else {
                // Si estamos creando un nuevo calendario, preguntar si quiere reiniciar el formulario
                if (window.confirm(t('cancel_confirm'))) {
                // Reiniciar el formulario
                setTitle('');
                setDescription('');
                setSelectedMeetingType(meetingType.IN_PERSON); // Default to IN_PERSON
                setMeetingDuration(30);
                setMaxPeoplePerMeeting(1);
                setSelectedGoogleCalendarIntegration('');
                setHeadquartersName('');
                setMeetingAddress('');
                setContactPhone('');
                setSelectedTimezone(new Date().getTimezoneOffset() / -60); // Reset to system timezone
                setVirtualMeetingError(''); // Clear error                  // Resetear los días disponibles
                  const resetDays = daysAvailability.map(day => ({
                    ...day,
                    active: false,
                    timeBlocks: [{...defaultTimeBlock}]
                  }));
                  setDaysAvailability(resetDays);
                  setShowCustomHours(false);
                }
              }
            }}
          >
            {t('cancel')}
          </button>
        </div>
        
        {error && (
          <div className="alert alert-error mt-4">
            <p>{t(isEditMode ? 'update_error' : 'create_error')}</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default CreateSchedule;
